import API from '@/amplify/api';
import Constant from '@/common/constant/Constant';
import Logger from '@/common/utils/Logger';
import Utils from '@/common/utils/utils';
import translationDb from '@/common/utils/translationDb';
import translationStore from '@/common/utils/translationStore';
import { isTranslationActive } from '@/common/utils/translationUtils';
import { translationUpdateApi  } from '@/graphql/mutations';

const TAG = 'translationService';

// Guard rails so a bad nextToken loop or a huge dictionary cannot hang the app.
const PAGE_SIZE = 1000;
const MAX_PAGE = 20;

// Max source strings accepted by translationUpdateApi in one call.
// Keep in sync with Translation.TRANSLATION_MAX_TEXTS_PER_REQUEST (amplifysnsdemocommonlibs/opt/common/Translation.js).
export const MAX_TEXTS_PER_REQUEST = 200;

const queryTranslationDictionaryByOrgAndUsageDoc = /* GraphQL */ `
    query QueryTranslationDictionaryByOrgAndUsage(
        $affiliationOrganization: String
        $usageTargetLanguageUpdatedAt: ModelTranslationDictionaryTranslationDictionaryGSI1CompositeKeyConditionInput
        $limit: Int
        $nextToken: String
    ) {
        queryTranslationDictionaryByOrgAndUsage(
            affiliationOrganization: $affiliationOrganization
            usageTargetLanguageUpdatedAt: $usageTargetLanguageUpdatedAt
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                sourceText
                translatedText
                targetLanguage
                updatedAt
            }
            nextToken
        }
    }
`;


/**
 * Loads one usage for an org + target language.
 *
 * The sort key is the composite `usage#targetLanguage#updatedAt`, so the
 * condition is expressed with the generated composite-key input:
 *   full load -> beginsWith { usage, targetLanguage, updatedAt: '' }
 *   delta     -> between [{... updatedAt: since}, {... updatedAt: '~'}]
 *
 * Two details are load-bearing:
 *  - `updatedAt: ''` on beginsWith. The generated VTL appends a segment only when
 *    the value is not null, so omitting it yields "UI#en" - which also matches
 *    "UI#en-GB#...". The empty string produces the trailing '#'.
 *  - `between` rather than a bare `gt` for the delta: an unbounded comparison
 *    spills past "UI#en#..." into "UI#ja#..." lexicographically. '~' (0x7E) sorts
 *    above every character an ISO-8601 timestamp can contain.
 *
 * @param {string} [since] - previous high-water mark; omit for a full load.
 * @returns {Promise<{map: Object, maxUpdatedAt: string|null}|null>}
 *   map: { [sourceText]: translatedText }, {} when there is genuinely nothing.
 *   maxUpdatedAt: newest server updatedAt seen - the next high-water mark. Results
 *   come back ascending on updatedAt, so it stays correct when MAX_PAGE cuts the load.
 *   null when the load FAILED - the caller then keeps its cached map.
 */
export const getBulkTranslationMap = async (
    affiliationOrganization,
    usage,
    targetLanguage,
    since = null
) => {
    if (!affiliationOrganization || !usage || !targetLanguage) {
        return null;
    }

    const sortKeyCondition = since
        ? {
              between: [
                  { usage, targetLanguage, updatedAt: since },
                  { usage, targetLanguage, updatedAt: '~' }
              ]
          }
        : { beginsWith: { usage, targetLanguage, updatedAt: '' } };

    const map = {};
    let maxUpdatedAt = null;
    let nextToken = null;
    let page = 0;

    try {
        do {
            const res = await API.graphql({
                query: queryTranslationDictionaryByOrgAndUsageDoc,
                variables: {
                    affiliationOrganization,
                    usageTargetLanguageUpdatedAt: sortKeyCondition,
                    limit: PAGE_SIZE,
                    nextToken
                }
            });

            const data = res?.data?.queryTranslationDictionaryByOrgAndUsage;
            for (const item of data?.items || []) {
                // Belt and braces against a prefix that over-matches.
                if (item?.targetLanguage !== targetLanguage) continue;
                if (item?.sourceText && item?.translatedText) {
                    map[item.sourceText] = item.translatedText;
                }
                // ISO-8601 strings compare correctly as plain strings.
                if (item?.updatedAt && (!maxUpdatedAt || item.updatedAt > maxUpdatedAt)) {
                    maxUpdatedAt = item.updatedAt;
                }
            }

            nextToken = data?.nextToken || null;
            page += 1;
        } while (nextToken && page < MAX_PAGE);

        return { map, maxUpdatedAt };
    } catch (err) {
        Logger.error(`${TAG} getBulkTranslationMap`, err);
        return null;
    }
};

/**
 * Calls translationUpdateApi and returns the whole envelope, errorCode included.
 *
 * Callers that need to tell the user WHY something failed use this one; invokeTranslationUpdate
 * below keeps the older "data or null" shape for the callers that only care about the payload.
 *
 * `projectInuse` is optional on purpose: the actions that predate it must keep sending exactly the
 * same variables they send today.
 *
 * @returns {Promise<{data: Object|null, errorCode: string|null}>}
 */
export const invokeTranslationUpdateApi = async ({
    affiliationOrganization,
    projectInuse,
    userId,
    actionType,
    requestBody
}) => {
    try {
        const res = await API.graphql({
            query: translationUpdateApi,
            variables: {
                translationUpdateApiRequestDto: {
                    // The Lambda re-derives the org from userId and refuses a
                    // mismatch, so a stale org id here fails closed.
                    corporationInuse: affiliationOrganization,
                    ...(projectInuse ? { projectInuse } : {}),
                    userId,
                    actionType,
                    requestBody: JSON.stringify(requestBody)
                }
            }
        });

        const response = res?.data?.translationUpdateApi;
        if (!response?.status) {
            const errorCode = response?.errorCode || Constant.TRANSLATION_API_ERROR_CODE.INTERNAL_ERROR;
            Logger.error(`${TAG} ${actionType}`, errorCode);
            return { data: null, errorCode };
        }

        return { data: Utils.convertJSONDataToObject(response.data) || {}, errorCode: null };
    } catch (err) {
        Logger.error(`${TAG} ${actionType}`, err);
        return { data: null, errorCode: Constant.TRANSLATION_API_ERROR_CODE.INTERNAL_ERROR };
    }
};

/** Calls translationUpdateApi and unwraps its envelope. */
const invokeTranslationUpdate = async params => (await invokeTranslationUpdateApi(params)).data;

/**
 * Translates and persists a batch of short strings for one usage.
 * @returns {Promise<Object>} { [sourceText]: translatedText } - {} on any error.
 */
export const translateMissingTexts = async (
    affiliationOrganization,
    usage,
    targetLanguage,
    sourceTexts,
    userId
) => {
    if (!affiliationOrganization || !targetLanguage || Utils.isEmptyArray(sourceTexts)) {
        return {};
    }

    const isUi = usage === Constant.TRANSLATION_USAGE.UI;
    const result = await invokeTranslationUpdate({
        affiliationOrganization,
        userId,
        actionType: isUi ? Constant.TRANSLATION_ACTION.TRANSLATE_AUTO : Constant.TRANSLATION_ACTION.TRANSLATE_LABEL,
        requestBody: {
            usage,
            targetLanguage,
            sourceTexts: sourceTexts.slice(0, MAX_TEXTS_PER_REQUEST)
        }
    });

    return result?.translations || {};
};

/**
 * Translate all translatable fields of one content (ticket) on demand.
 *
 * The client sends only ids + field types (sourceType, sourceId, listField) - NOT
 * the content: the Lambda fetches the content itself (e.g. ticket via GSI3),
 * translates every field (using the field type to pick the mimeType), writes one
 * result file to S3 and returns a presigned URL. We then fetch that URL to get the
 * { [fieldId]: translatedText } map. Nothing is persisted in DynamoDB. Target
 * language is decided by the BE for now.
 *
 * @param {Object} params
 * @param {string} params.affiliationOrganization
 * @param {string} params.userId
 * @param {string} params.sourceType - TranslationConstant.ON_DEMAND_SOURCE_TYPE
 * @param {string} params.sourceId - the content id (ticketId)
 * @param {Array<{ fieldId: string, type: string }>} params.listField - fields to translate
 * @param {string} [params.outline] - the title/outline text to translate (returned under the
 *   reserved outline key in the result map).
 * @returns {Promise<{translations: Object|null, errorCode: string|null}>}
 *   translations: { [fieldId]: translatedText, [outline]: translatedOutline } on success,
 *   or null on failure so the caller can keep showing the original.
 *   errorCode: the BE error code (Constant.TRANSLATION_API_ERROR_CODE) when the call failed,
 *   null on success. The caller uses it to tell a stale-data failure (INVALID_PARAM -> ask the
 *   user to reload) apart from a generic failure.
 */
export const translateContentOnDemand = async (
    affiliationOrganization,
    userId,
    translateArgs
) => {
    if (!affiliationOrganization || !userId || Utils.isEmptyObject(translateArgs)) {
        return { translations: null, errorCode: Constant.TRANSLATION_API_ERROR_CODE.INVALID_PARAM };
    }

    const result = await invokeTranslationUpdateApi({
        affiliationOrganization,
        userId,
        actionType: Constant.TRANSLATION_ACTION.TRANSLATE_ONDEMAND,
        requestBody: { ...translateArgs }
    });
    const { data, errorCode } = result;

    if (errorCode) {
        return { translations: null, errorCode };
    }

    const dataUrl = data?.dataUrl;
    if (!dataUrl) {
        return { translations: null, errorCode: Constant.TRANSLATION_API_ERROR_CODE.INTERNAL_ERROR };
    }

    // The translated field map lives in S3 (offloaded to avoid AppSync response limits). Fetch it.
    try {
        const res = await fetch(dataUrl);
        if (!res.ok) {
            return { translations: null, errorCode: Constant.TRANSLATION_API_ERROR_CODE.INTERNAL_ERROR };
        }
        return { translations: await res.json(), errorCode: null };
    } catch (e) {
        Logger.error(`${TAG} translateContentOnDemand fetch S3`, e);
        return { translations: null, errorCode: Constant.TRANSLATION_API_ERROR_CODE.INTERNAL_ERROR };
    }
};

/**
 * 翻訳管理 grid: search 原文 (SOURCE docs).
 *
 * @param {Object} params
 * @param {string} params.affiliationOrganization
 * @param {string} params.userId
 * @param {string} [params.keyword]
 * @param {string} [params.usage]     - 種別 filter.
 * @param {string} [params.sourceLanguage] - 翻訳元言語 filter.
 * @param {string} [params.targetLanguage] - 翻訳先言語 filter.
 * @param {string} [params.status]    - 状態 filter (AUTO | CORRECTED | MANUAL | UNTRANSLATED).
 * @param {string} [params.useAsTerminology] - 用語 filter ('' = all | Y = ON | N = OFF).
 * @param {number} [params.from]      - paging offset.
 * @param {number} [params.size]      - page size.
 * @param {string} [params.sortField] - sourceText | updatedAt | createdAt.
 * @param {string} [params.sortOrder] - asc | desc.
 * @returns {Promise<{ total: number, items: object[] }>} - empty on error.
 */
export const searchTranslationSource = async ({
    affiliationOrganization,
    userId,
    keyword = '',
    usage = '',
    sourceLanguage = '',
    targetLanguage = '',
    status = '',
    useAsTerminology = '',
    from = 0,
    size = 50,
    sortField = 'sourceText',
    sortOrder = 'asc',
    withOrgLanguages = false
}) => {
    const result = await invokeTranslationUpdate({
        affiliationOrganization,
        userId,
        actionType: Constant.TRANSLATION_ACTION.SEARCH_SOURCE_TRANSLATION,
        requestBody: {
            keyword,
            usage,
            sourceLanguage,
            targetLanguage,
            status,
            useAsTerminology,
            from,
            size,
            sortField,
            sortOrder,
            withOrgLanguages
        }
    });

    return result && Array.isArray(result.items) ? result : { total: 0, items: [] };
};

/**
 * 翻訳管理: expand one 原文 into its per-language translations (ROW docs).
 *
 * @param {Object} params
 * @param {string} params.affiliationOrganization
 * @param {string} params.userId
 * @param {string} params.sourceHash
 * @param {string} [params.usage]
 * @param {string} [params.sourceLanguage] - the 原文's language; excluded from the languages listed.
 * @returns {Promise<{ total: number, items: object[] }>} - empty on error. Items include
 *   placeholders (no `id`, empty translatedText) for every org language not translated yet.
 */
export const expandTranslationRow = async ({
    affiliationOrganization,
    userId,
    sourceHash,
    usage = '',
    sourceLanguage = ''
}) => {
    if (!sourceHash) {
        return { total: 0, items: [] };
    }

    const result = await invokeTranslationUpdate({
        affiliationOrganization,
        userId,
        actionType: Constant.TRANSLATION_ACTION.EXPAND_ROW_TRANSLATION,
        requestBody: { sourceHash, usage, sourceLanguage }
    });

    return result && Array.isArray(result.items) ? result : { total: 0, items: [] };
};

/**
 * 翻訳管理: toggle the 用語 (glossary) flag on a 原文. Q1=A - the flag applies to
 * every language row sharing the sourceHash, so the caller passes all of them.
 *
 * @param {Object} params
 * @param {string} params.affiliationOrganization
 * @param {string} params.userId
 * @param {string} params.usage
 * @param {string} params.sourceText
 * @param {boolean} params.useAsTerminology
 * @param {string[]} params.targetLanguages - every language of this 原文.
 * @returns {Promise<{ updated: number, failed: string[] }|null>}
 */
export const toggleTerminology = async ({
    affiliationOrganization,
    userId,
    usage,
    sourceText,
    sourceHash = '',
    useAsTerminology,
    targetLanguages
}) => {
    if (!usage || !sourceText || Utils.isEmptyArray(targetLanguages)) {
        return null;
    }

    return invokeTranslationUpdate({
        affiliationOrganization,
        userId,
        actionType: Constant.TRANSLATION_ACTION.UPSERT_TERMINOLOGY,
        // sourceHash lets the server find the rows of a Terminology entry.
        requestBody: { usage, sourceText, sourceHash, useAsTerminology, targetLanguages }
    });
};

/**
 * 翻訳管理: replace a machine translation with a hand-corrected one. The row's
 * status becomes CORRECTED, so sync will not overwrite it.
 *
 * @param {Object} params
 * @param {string} params.affiliationOrganization
 * @param {string} params.userId
 * @param {string} params.usage
 * @param {string} params.targetLanguage
 * @param {string} params.sourceText
 * @param {string} params.translatedText
 * @param {boolean} [params.isAutoTranslated=false] - true when the text comes from the
 *   translate icon (re-translate): stored as 自動 instead of 修正済.
 * @returns {Promise<{data: {corrected: boolean}|null, errorCode: string|null}>}
 */
export const correctTranslation = async ({
    affiliationOrganization,
    userId,
    usage,
    targetLanguage,
    sourceText,
    translatedText,
    isAutoTranslated = false
}) => {
    if (!usage || !targetLanguage || !sourceText || Utils.isEmptyString(translatedText)) {
        return { data: null, errorCode: Constant.TRANSLATION_API_ERROR_CODE.INVALID_PARAM };
    }

    return invokeTranslationUpdateApi({
        affiliationOrganization,
        userId,
        actionType: Constant.TRANSLATION_ACTION.CORRECT_TRANSLATION,
        requestBody: { usage, targetLanguage, sourceText, translatedText, isAutoTranslated }
    });
};

/**
 * 翻訳管理: create a Terminology entry (keyword + translations) in one step.
 * The server validates everything (required, length, Japanese translation, duplicate).
 *
 * @param {Object} params
 * @param {string} params.affiliationOrganization
 * @param {string} params.userId
 * @param {string} params.sourceLanguage - 翻訳元言語 picked by the admin
 * @param {string} params.sourceText - the keyword
 * @param {Array<{targetLanguage: string, translatedText: string}>} params.translations
 * @param {boolean} [params.useAsTerminology=true] - コンテンツ翻訳に利用
 * @returns {Promise<{data: Object|null, errorCode: string|null}>} data = the new grid item
 */
export const createTerminologyEntry = async ({
    affiliationOrganization,
    userId,
    sourceLanguage,
    sourceText,
    translations,
    useAsTerminology = true
}) =>
    invokeTranslationUpdateApi({
        affiliationOrganization,
        userId,
        actionType: Constant.TRANSLATION_ACTION.CREATE_TERMINOLOGY,
        requestBody: { sourceLanguage, sourceText, translations, useAsTerminology }
    });

/**
 * 翻訳管理: edit the keyword and/or translations of a Terminology entry. The entry is
 * identified by its sourceHash (stable for the life of the entry).
 *
 * @param {Object} params
 * @param {string} params.sourceHash
 * @param {string} [params.sourceText] - the keyword; omitted = unchanged
 * @param {Array<{targetLanguage: string, translatedText: string}>} [params.translations]
 * @returns {Promise<{data: Object|null, errorCode: string|null}>} data = { item, rows }
 */
export const updateTerminologyEntry = async ({ affiliationOrganization, userId, sourceHash, sourceText, translations = [] }) =>
    invokeTranslationUpdateApi({
        affiliationOrganization,
        userId,
        actionType: Constant.TRANSLATION_ACTION.UPDATE_TERMINOLOGY,
        requestBody: { sourceHash, sourceText, translations }
    });

/**
 * 翻訳管理: delete one 原文 of any usage (UI / FIELD_LABEL / TAG / TERMINOLOGY)
 * with all its translations. A system usage is translated again on next use.
 * @returns {Promise<{data: Object|null, errorCode: string|null}>}
 */
export const deleteTranslationEntry = async ({ affiliationOrganization, userId, usage, sourceHash }) =>
    invokeTranslationUpdateApi({
        affiliationOrganization,
        userId,
        actionType: Constant.TRANSLATION_ACTION.DELETE_TRANSLATION_ENTRY,
        requestBody: { usage, sourceHash }
    });

/**
 * Drops a just-deleted entry from this browser's auto-translation cache, so tr()
 * misses on it and it is translated again without a reload. Other languages and
 * other browsers are wiped on their next load via Corporation.translationCacheResetAt.
 */
export const evictLocalTranslation = async ({ affiliationOrganization, usage, sourceText }) => {
    const config = translationStore.getConfig();
    if (!isTranslationActive(config) || !Constant.TRANSLATION_CACHE_RESET_USAGES.includes(usage)) return;
    if (!affiliationOrganization || Utils.isEmptyString(sourceText)) return;

    translationStore.removeMany(usage, [sourceText]);
    await translationDb.deleteEntry(affiliationOrganization, usage, config.mainLanguage, sourceText);
};

/**
 * 翻訳管理: machine-translate one text as a suggestion. Nothing is saved.
 * @returns {Promise<string>} the suggestion, '' on failure.
 */
export const previewTranslate = async ({ affiliationOrganization, userId, text, sourceLanguage, targetLanguage }) => {
    if (Utils.isEmptyString(text) || !sourceLanguage || !targetLanguage) return '';

    const result = await invokeTranslationUpdate({
        affiliationOrganization,
        userId,
        actionType: Constant.TRANSLATION_ACTION.PREVIEW_TRANSLATE,
        requestBody: { text, sourceLanguage, targetLanguage }
    });
    return result?.translatedText || '';
};

const translationService = {
    getBulkTranslationMap,
    translateMissingTexts,
    translateContentOnDemand,
    searchTranslationSource,
    expandTranslationRow,
    toggleTerminology,
    correctTranslation,
    createTerminologyEntry,
    updateTerminologyEntry,
    deleteTranslationEntry,
    evictLocalTranslation,
    previewTranslate,
    MAX_TEXTS_PER_REQUEST
};

export default translationService;
